import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../../utils/functions/api';
import { fmtDateTime } from './constants';

import { brandHex } from '../../../utils/orgConfig';
const apiBase = process.env.REACT_APP_API_URL;

// 3 สถานะของผู้รับแต่ละคน — seen = เห็น popup แล้วแต่ยังไม่กดรับทราบ
const STATES = [
  { key: 'done', label: 'รับทราบแล้ว', color: '#10b981', Icon: CheckCircleIcon },
  { key: 'seen', label: 'เห็นแล้ว ยังไม่รับทราบ', color: '#f59e0b', Icon: VisibilityOutlinedIcon },
  { key: 'unseen', label: 'ยังไม่เปิดดู', color: '#9ca3af', Icon: RadioButtonUncheckedIcon },
];
const STATE_BY_KEY = Object.fromEntries(STATES.map((s) => [s.key, s]));

const stateOf = (r) => {
  if (r.acknowledged_at) return 'done';
  if (r.seen_at) return 'seen';
  return 'unseen';
};

function ProgressRing({ pct }) {
  const color = pct >= 100 ? '#10b981' : brandHex(500);
  return (
    <Box sx={{ position: 'relative', width: 104, height: 104, flexShrink: 0 }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={104}
        thickness={5}
        sx={{ color: alpha(color, 0.12), position: 'absolute' }}
      />
      <CircularProgress
        variant="determinate"
        value={pct}
        size={104}
        thickness={5}
        sx={{ color, position: 'absolute', '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }}
      />
      <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0 }}>
        <Typography fontWeight={700} fontSize="1.4rem" lineHeight={1}>{pct}%</Typography>
        <Typography variant="caption" color="text.secondary">รับทราบ</Typography>
      </Stack>
    </Box>
  );
}

function StatButton({
  state, count, selected, onClick,
}) {
  const { Icon } = state;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.25,
        borderRadius: 2.5,
        cursor: 'pointer',
        border: '1.5px solid',
        borderColor: selected ? state.color : 'transparent',
        bgcolor: alpha(state.color, selected ? 0.1 : 0.06),
        transition: 'all 0.15s ease',
        outline: 'none',
        '&:hover': { bgcolor: alpha(state.color, 0.12) },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Icon sx={{ fontSize: 16, color: state.color }} />
        <Typography fontWeight={700} fontSize="1.15rem" lineHeight={1}>{count}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ mt: 0.5 }}>
        {state.label}
      </Typography>
    </Box>
  );
}

// รายชื่อผู้รับประกาศ + ใครรับทราบแล้ว/เห็นแล้ว/ยังไม่เปิดดู
export default function AnnouncementReadReportDialog({ announcement, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [report, setReport] = useState(null);
  const [filter, setFilter] = useState(null); // null = ทุกคน
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!announcement) return undefined;
    let cancelled = false;
    setReport(null);
    setFilter(null);
    setSearch('');
    api.get(`${apiBase}/announcement/${announcement.id}/read-report`).then(({ data }) => {
      if (!cancelled) setReport(data);
    });
    return () => { cancelled = true; };
  }, [announcement]);

  const rows = useMemo(() => (report?.rows || []).map((r) => ({ ...r, state: stateOf(r) })), [report]);

  const counts = useMemo(() => {
    const c = { done: 0, seen: 0, unseen: 0 };
    rows.forEach((r) => { c[r.state] += 1; });
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => (!filter || r.state === filter)
      && (!q || [r.name, r.employee_id, r.department].some((v) => String(v || '').toLowerCase().includes(q))));
  }, [rows, filter, search]);

  const pct = report?.total ? Math.round((report.acknowledged / report.total) * 100) : 0;

  return (
    <Dialog
      open={!!announcement}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3, height: fullScreen ? '100%' : '85vh' } }}
    >
      {/* หัว modal */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            background: 'linear-gradient(135deg, rgb(var(--color-primary-450)) 0%, rgb(var(--color-primary-500)) 60%, rgb(var(--color-primary-600)) 100%)',
            boxShadow: '0 4px 12px rgb(var(--color-primary-500) / 0.3)',
            flexShrink: 0,
          }}
        >
          <FactCheckOutlinedIcon fontSize="small" />
        </Box>
        <Box flexGrow={1} minWidth={0}>
          <Typography fontWeight={700} fontSize="1.1rem" lineHeight={1.2}>การรับทราบ</Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {announcement?.title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Stack>

      {/* สรุป + ตัวกรอง */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
        {!report ? (
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Skeleton variant="circular" width={104} height={104} />
            <Stack direction="row" spacing={1} flexGrow={1}>
              {STATES.map((s) => <Skeleton key={s.key} variant="rounded" height={60} sx={{ flex: 1 }} />)}
            </Stack>
          </Stack>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems="center">
            <ProgressRing pct={pct} />
            <Box flexGrow={1} width="100%">
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                ผู้รับทั้งหมด <b style={{ color: theme.palette.text.primary }}>{report.total}</b> คน
                {filter && (
                  <Typography
                    component="span"
                    variant="caption"
                    onClick={() => setFilter(null)}
                    sx={{ ml: 1, color: 'primary.main', cursor: 'pointer' }}
                  >
                    แสดงทุกคน
                  </Typography>
                )}
              </Typography>
              <Stack direction="row" spacing={1}>
                {STATES.map((s) => (
                  <StatButton
                    key={s.key}
                    state={s}
                    count={counts[s.key]}
                    selected={filter === s.key}
                    onClick={() => setFilter(filter === s.key ? null : s.key)}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        )}

        <TextField
          size="small"
          fullWidth
          placeholder="ค้นหาชื่อ รหัส หรือแผนก"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mt: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>
            ),
          }}
        />
      </Box>

      {/* รายชื่อ */}
      <DialogContent sx={{ px: 1.5, pt: 0, borderTop: '1px solid', borderColor: 'divider' }}>
        {report && visible.length === 0 && (
          <Typography color="text.secondary" align="center" sx={{ py: 6 }}>ไม่มีรายชื่อ</Typography>
        )}
        {visible.map((r) => {
          const st = STATE_BY_KEY[r.state];
          const time = r.state === 'done' ? r.acknowledged_at : r.seen_at;
          return (
            <Stack
              key={`${r.employee_id}-${r.name}`}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  bgcolor: alpha(st.color, 0.15),
                  color: st.color === '#9ca3af' ? 'text.secondary' : st.color,
                }}
              >
                {(r.name || '?').charAt(0)}
              </Avatar>
              <Box flexGrow={1} minWidth={0}>
                <Typography variant="body2" fontWeight={600} noWrap>{r.name || '-'}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {[r.employee_id, r.department].filter(Boolean).join(' · ') || '-'}
                </Typography>
              </Box>
              <Box textAlign="right" flexShrink={0}>
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                  <st.Icon sx={{ fontSize: 15, color: st.color }} />
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ color: st.color === '#9ca3af' ? 'text.secondary' : st.color }}
                  >
                    {st.label}
                  </Typography>
                </Stack>
                {time && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {fmtDateTime(time)}
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        })}
      </DialogContent>
    </Dialog>
  );
}
